/**
 * Dynamic API URL Resolver
 * Determines the API URL based on the current domain
 */

interface ApiConfig {
  apiUrl: string;
  pathBase: string;
  useMockData: boolean;
}

/**
 * Domain to API URL mapping patterns
 */
const DOMAIN_MAPPINGS: Record<string, ApiConfig> = {
  // Production
  'skillzen.ai': {
    apiUrl: 'https://api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  'www.skillzen.ai': {
    apiUrl: 'https://api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  
  // Nightly/Staging environments
  'nightly.skillzen.ai': {
    apiUrl: 'https://nightly-api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  'staging.skillzen.ai': {
    apiUrl: 'https://staging-api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  'dev.skillzen.ai': {
    apiUrl: 'https://dev-api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  'demo.skillzen.ai': {
    apiUrl: 'https://demo-api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  // Preview deployments (Cloudflare Pages)
  'preview.skillzen.ai': {
    apiUrl: 'https://preview-api.skillzen.ai',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  
  // Local development
  'localhost': {
    apiUrl: 'http://localhost:5011',
    pathBase: '/skillzen-srv',
    useMockData: false
  },
  '127.0.0.1': {
    apiUrl: 'http://localhost:5011',
    pathBase: '/skillzen-srv',
    useMockData: false
  }
};

/**
 * Pattern-based resolver for dynamic subdomains
 */
function resolveByPattern(hostname: string): ApiConfig | null {
  // Check for Cloudflare Pages preview URLs
  if (hostname.includes('.pages.dev')) {
    // Extract subdomain from Cloudflare Pages URL
    // Format: [branch-name].[project].pages.dev
    const match = hostname.match(/^([^.]+)\.skillzen-v2-ui\.pages\.dev$/);
    if (match) {
      const branch = match[1];
      if (branch === 'main') {
        return DOMAIN_MAPPINGS['skillzen.ai'];
      }
      // For feature branches, use dev API
      return {
        apiUrl: 'https://dev-api.skillzen.ai',
        pathBase: '/skillzen-srv',
        useMockData: false
      };
    }
  }
  
  // Check for custom subdomain pattern: [env].skillzen.ai -> [env]-api.skillzen.ai
  const subdomainMatch = hostname.match(/^([^.]+)\.skillzen\.ai$/);
  if (subdomainMatch) {
    const subdomain = subdomainMatch[1];
    
    // Check if we have a specific mapping first
    if (DOMAIN_MAPPINGS[hostname]) {
      return DOMAIN_MAPPINGS[hostname];
    }
    
    // Generate API URL based on subdomain pattern
    return {
      apiUrl: `https://${subdomain}-api.skillzen.ai`,
      pathBase: '/skillzen-srv',
      useMockData: false
    };
  }
  
  // Check for custom domain with port (local development)
  if (hostname.includes(':')) {
    const [domain] = hostname.split(':');
    if (domain === 'localhost' || domain === '127.0.0.1') {
      return DOMAIN_MAPPINGS['localhost'];
    }
  }
  
  return null;
}

/**
 * Get API configuration based on current environment
 */
export function getApiConfig(): ApiConfig {
  // Priority 1: Check for explicit environment variable overrides
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL !== 'AUTO') {
    return {
      apiUrl: import.meta.env.VITE_API_URL,
      pathBase: import.meta.env.VITE_PATH_BASE_URL || '/skillzen-srv',
      useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true'
    };
  }
  
  // Priority 2: Determine from current hostname
  const hostname = window.location.hostname;
  const port = window.location.port;
  const fullHost = port ? `${hostname}:${port}` : hostname;
  
  console.log('Resolving API URL for hostname:', fullHost);
  
  // Check exact match first
  if (DOMAIN_MAPPINGS[hostname]) {
    console.log('Found exact match for hostname:', hostname);
    return DOMAIN_MAPPINGS[hostname];
  }
  
  // Try pattern-based resolution
  const patternConfig = resolveByPattern(fullHost);
  if (patternConfig) {
    console.log('Resolved by pattern:', patternConfig);
    return patternConfig;
  }
  
  // Priority 3: Fallback to environment variables or defaults
  console.warn('No domain mapping found, using fallback configuration');
  return {
    apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:5011',
    pathBase: import.meta.env.VITE_PATH_BASE_URL || '/skillzen-srv',
    useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true' || false
  };
}

/**
 * Get the full API base URL
 */
export function getApiBaseUrl(): string {
  const config = getApiConfig();
  return `${config.apiUrl}${config.pathBase}`;
}

/**
 * Check if mock data should be used
 */
export function shouldUseMockData(): boolean {
  const config = getApiConfig();
  return config.useMockData;
}

/**
 * Get current environment name
 */
export function getEnvironmentName(): string {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  if (hostname === 'skillzen.ai' || hostname === 'www.skillzen.ai') {
    return 'production';
  }
  
  if (hostname.includes('.skillzen.ai')) {
    const subdomain = hostname.split('.')[0];
    return subdomain;
  }
  
  if (hostname.includes('.pages.dev')) {
    return 'preview';
  }
  
  return 'unknown';
}

/**
 * Log current API configuration (for debugging)
 */
export function logApiConfig(): void {
  const config = getApiConfig();
  const env = getEnvironmentName();
  
  console.group('🔧 API Configuration');
  console.log('Environment:', env);
  console.log('Hostname:', window.location.hostname);
  console.log('API URL:', config.apiUrl);
  console.log('Path Base:', config.pathBase);
  console.log('Full API URL:', getApiBaseUrl());
  console.log('Use Mock Data:', config.useMockData);
  console.groupEnd();
}

// Auto-log in development mode
if (import.meta.env.DEV) {
  logApiConfig();
}